import { MediaContent, GetRelevantSourcesResponse, MediaContentResponse } from '@goldcast/api/content';
import moment from 'moment';
import { TextType } from '@goldcast/player-config/src/types/asset-types';
import {
  isAlignedStreamProcessing,
  isEventTypeRecording,
  isLiveEventRecording
} from '@goldcast/player-config/src/utils/mediaContentUtils';
import { BROADCAST_UNAVAILABLE_REASONS, PREVIEW_IMAGE_SIZES, PreviewImageSize } from './constants';
import type { NewUploadBadge, SessionPreviewState } from './types';
import appLogger from '@/services/logger';
import { getEnvConfig } from '@/constants';
import { SESSION_PREVIEW_STATUS_MAP } from '@/Pages/Sessions/constants';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { isScreenShareDetectionFeatureFlagEnabled, isSimplifiedUploadEnabled } from '@/stores/clip';
import {
  isClipsTerminalFromContent,
  isFacialRecTerminalFromContent,
  isScreenshareTerminalFromContent
} from '@/hooks/useUploadEditorReadiness';

const gradients = [
  'bg-gradient-to-tr from-green-300 via-blue-500 to-purple-600 before:bg-gradient-to-tr before:from-green-300 before:via-blue-500 before:to-purple-600',
  'bg-gradient-to-tr from-pink-300 via-purple-300 to-indigo-400 before:bg-gradient-to-tr before:from-pink-300 before:via-purple-300 before:to-indigo-400',
  'bg-gradient-to-tr from-red-200 via-red-300 to-yellow-200 before:bg-gradient-to-tr before:from-red-200 before:via-red-300 before:to-yellow-200',
  'bg-gradient-to-tr from-pink-400 to-pink-600 before:bg-gradient-to-tr before:from-pink-400 before:to-pink-600',
  'bg-gradient-to-tr from-rose-400 via-fuchsia-500 to-indigo-500 before:bg-gradient-to-tr before:from-pink-400 before:to-pink-600'
];

export const gradientTextAssetMapping: Record<TextType, string> = {
  LINKEDIN: gradients[0],
  EMAIL: gradients[1],
  BLOG: gradients[2],
  TAKEAWAYS: gradients[3],
  CUSTOM: gradients[4]
};

export const getTitleFromMarkdownString = (markdownString: string) => {
  if (!markdownString) {
    return '';
  }

  const match = markdownString.match(/^#+\s+(.*)/m);

  if (match) {
    return match[1];
  }
  appLogger.warn('No title found in Markdown string');
  return markdownString?.substring(0, 100).replace(/[#*]/g, '');
};

export const parseTextTitle = (title: string) => {
  if (!title) {
    return '';
  }

  return title
    .replaceAll(new RegExp(/^(#+\s+)/, 'g'), '') // Remove Markdown heading markers at the start (e.g. "# ", "## ", "### ")
    .replaceAll(new RegExp(/<u>(.*?)<\/u>/g), '$1') // Remove underline HTML tags: <u>text</u> -> text
    .replaceAll(new RegExp(/~~(.*?)~~/g), '$1') // Remove Markdown strikethrough: ~~text~~ -> text
    .replaceAll(new RegExp(/\*\*\*(.*?)\*\*\*/g), '$1') // Remove bold + italic Markdown: ***text*** -> text
    .replaceAll(new RegExp(/\*\*(.*?)\*\*/g), '$1') // Remove bold Markdown: **text** -> text
    .replaceAll(new RegExp(/\*(.*?)\*/g), '$1') // Remove italic Markdown: *text* -> text
    .trim();
};

export type TypeOptionsType = {
  label: string;
  value: 'CLIP' | 'TEXT';
};

export const typeOptions: TypeOptionsType[] = [
  {
    label: 'Clip',
    value: 'CLIP'
  },
  {
    label: 'Text',
    value: 'TEXT'
  }
];

export const formatToStandardDate = (date: string) => {
  return moment(date).format('MMMM D, YYYY');
};

export function getBroadcastBackgroundImageUrl(
  content: MediaContent | GetRelevantSourcesResponse | MediaContentResponse,
  size: PreviewImageSize = PreviewImageSize.Small
): string {
  const sizeString = PREVIEW_IMAGE_SIZES[size];
  if (content.media_source_type === 'UPLOAD') {
    return `${getEnvConfig('VUE_APP_VID_CDN_URL')}${content.project.id}/${
      content.id
    }/recording/subtitles/thumbnail_${sizeString}.png`;
  }
  return `${getEnvConfig('CLOUDFRONT_STATIC_ASSET_URL')}screenshots/reg/${content.project.id}-${sizeString}.png`;
}

export function convertBroadcastReason(reason: string): string {
  return BROADCAST_UNAVAILABLE_REASONS[reason] || '';
}

export function isRecordingStudioPreviewNotReady(session: MediaContent) {
  if (session.event_type !== 'Recording') {
    return false;
  }

  if (session.batch_status !== 'DONE') {
    return false;
  }

  const rsTestDate = Number(featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Test_RS_Date]);
  const createdAtGreaterThanRecordingStudioTestDate =
    rsTestDate && moment(session.live_end_time).isAfter(moment(rsTestDate));

  if (!createdAtGreaterThanRecordingStudioTestDate) {
    return false;
  }

  if (session.individual_recording_status === 'DONE') {
    return false;
  }

  return true;
}

export function getSessionPreviewDetails(session: MediaContent) {
  const batchStatus = session.batch_status as string;
  const isVideo = session.media_type === 'VIDEO';

  const isBatchProcessingOrUploading =
    batchStatus === 'PROCESSING' || batchStatus === 'UPLOADING' || batchStatus === 'MULTI_LANGUAGE_SUPPORT_RESTRICTED';

  const isBatchDone = batchStatus === 'DONE' || batchStatus === 'VOD_DONE';
  const isGeneratingPostProcessingAssets =
    isBatchDone &&
    session.embedding_generation_status !== null &&
    (session.clip_generation_status === 'PROCESSING' ||
      session.clip_generation_status === null ||
      (isVideo && session.ftux_facial_recognition_status === 'PROCESSING'));

  const isContentUploadInProgress = isBatchProcessingOrUploading || isGeneratingPostProcessingAssets;

  let contentStatusText = SESSION_PREVIEW_STATUS_MAP[session.batch_status] || 'Unavailable';

  // Simplified upload replaces the Generating Clips state — the badge is driven by
  // upload_editor_status instead (handled in getNewUploadBadge).
  if (isGeneratingPostProcessingAssets && !(isSimplifiedUploadEnabled() && session.media_source_type === 'UPLOAD')) {
    contentStatusText = SESSION_PREVIEW_STATUS_MAP['GENERATING'];
  }

  if (isRecordingStudioPreviewNotReady(session) || isLiveEventReadyForPreview(session) === false) {
    contentStatusText = SESSION_PREVIEW_STATUS_MAP['PROCESSING'];
  }

  return { isContentUploadInProgress, contentStatusText };
}

const isLiveEventReadyForPreview = (session: MediaContent) => {
  if (!isLiveEventRecording(session.media_source_type, session.event_type, session.broadcast_type)) {
    // this is not a live event, I don't know whether content is ready for preview
    return null;
  }
  if (isAlignedStreamProcessing(session.individual_recording_status)) {
    return false;
  }
  return true;
};

export function isSessionReadyForPreview(session: MediaContent) {
  if (isRecordingStudioPreviewNotReady(session)) {
    return false;
  }

  if (isLiveEventReadyForPreview(session) === false) {
    return false;
  }

  const batchStatus = session.batch_status as string;
  const isPreviewableBatchStatus =
    batchStatus === 'DONE' || batchStatus === 'VOD_DONE' || batchStatus === 'MULTI_LANGUAGE_SUPPORT_RESTRICTED';

  if (!isPreviewableBatchStatus) {
    return false;
  }

  if (session.media_type === 'VIDEO') {
    if (['NOT_APPLICABLE', 'DONE', null].includes(session.ftux_facial_recognition_status)) {
      return true;
    }
  } else {
    if (session.clip_generation_status === 'DONE') {
      return true;
    }
  }

  if (session.clip_generation_status === 'FAILED' || session.clip_generation_status === 'LIMIT_REACHED') {
    return true;
  }

  if (!session.embedding_generation_status && !session.clip_generation_status) {
    return true;
  }

  return false;
}

function getNewUploadBadge(session: MediaContent): NewUploadBadge {
  if (!isSimplifiedUploadEnabled() || session.media_source_type !== 'UPLOAD') {
    return null;
  }
  const uploadEditorStatus = session.upload_editor_status as string | null | undefined;
  if (uploadEditorStatus === 'DONE') {
    const allTerminal =
      isClipsTerminalFromContent(session) &&
      (!isScreenShareDetectionFeatureFlagEnabled() || isScreenshareTerminalFromContent(session)) &&
      isFacialRecTerminalFromContent(session);
    if (!allTerminal) {
      return {
        text: SESSION_PREVIEW_STATUS_MAP['ANALYSING'],
        variation: 'orange100',
        iconType: 'spinner',
        tooltip: 'Video is ready to edit. Speaker detection, layouts, and AI content are still analysing.'
      };
    }
  } else if (uploadEditorStatus === 'PROCESSING') {
    // Editor still processing — gray Processing badge alongside the placeholder thumbnail.
    return { text: SESSION_PREVIEW_STATUS_MAP['PROCESSING'], variation: 'gray', iconType: 'none' };
  }
  return null;
}

type SimplifiedUploadCardStatus = 'UPLOADING' | 'PROCESSING' | 'DONE' | 'FAILED';

/**
 * Single source of truth for a simplified-upload UPLOAD card. Behind the flag the card never reads
 * raw batch_status: the backend's computed `upload_editor_status` (spec §3.3) already folds the whole
 * transcription pipeline — including the DONE/VOD_DONE equivalence — into one of PROCESSING | DONE |
 * FAILED, and is always present on the record. The only state it cannot express is the raw
 * client->Filestack transfer, which happens before a backend record exists; useMediaContent stands in
 * an optimistic placeholder for that phase (`__isOptimistic`, batch_status 'UPLOADING').
 */
function getSimplifiedUploadCardStatus(session: MediaContent): SimplifiedUploadCardStatus {
  const isOptimistic = (session as { __isOptimistic?: boolean }).__isOptimistic === true;
  if (isOptimistic && (session.batch_status as string) === 'UPLOADING') {
    return 'UPLOADING';
  }
  const status = session.upload_editor_status as SimplifiedUploadCardStatus | undefined;
  if (status === 'DONE' || status === 'FAILED' || status === 'PROCESSING') {
    return status;
  }
  // Optimistic card that has advanced past the raw transfer, or a record whose status isn't computed
  // yet — treat as still processing.
  return 'PROCESSING';
}

function getSimplifiedUploadPreviewState(session: MediaContent): SessionPreviewState {
  const projectId = session.project.id || session.project_id;
  const sessionId = session.id;
  const status = getSimplifiedUploadCardStatus(session);

  const isUploading = status === 'UPLOADING';
  const isProcessing = status === 'PROCESSING';
  const isDone = status === 'DONE';
  const isFailed = status === 'FAILED';

  // Real thumbnail + hover affordance only once the editor is ready.
  const canPreviewSession = isDone;
  const isVideoReadyForPreview = isDone;

  // Openable only once the editor is ready (DONE) — there is nothing to open before then: the video
  // can't be edited while processing, and the readiness popup already surfaces progress/errors. So
  // UPLOADING, PROCESSING and FAILED are all non-openable (cursor-not-allowed).
  const canOpenSession = isDone;
  const isDisabled = !canOpenSession;
  const isContentUploadInProgress = isUploading || isProcessing;

  const newUploadBadge = getNewUploadBadge(session);

  // Shown by the header placeholder (and the footer's fallback badge) whenever the thumbnail is
  // hidden. DONE never renders this — the header shows the thumbnail and the footer the Analysing/no
  // badge instead.
  const contentStatusText = isUploading
    ? SESSION_PREVIEW_STATUS_MAP['UPLOADING']
    : isProcessing
      ? SESSION_PREVIEW_STATUS_MAP['PROCESSING']
      : 'Unavailable';

  const isBatchFailed = isFailed;
  // Which failure icon to show still legitimately reads the raw `reason` (spec §4.5 sanctions the raw
  // signal for the *specific* failure reason).
  const failedIconName = session.reason === 'LANGUAGE_NOT_SUPPORTED' ? 'IconLanguageOff' : 'IconSquareRoundedX';

  const endTime =
    session.broadcast_type === 'LIVE_HYBRID' ? session.live_end_time || session.end_time : session.end_time;

  return {
    projectId,
    sessionId,
    isContentUploadInProgress,
    contentStatusText,
    canPreviewSession,
    canOpenSession,
    isRecordingEventProcessing: false,
    isVideoReadyForPreview,
    isDisabled,
    isBatchFailed,
    failedIconName,
    endTime,
    newUploadBadge
  };
}

export function getSessionPreviewState(session: MediaContent): SessionPreviewState {
  // Simplified-upload UPLOAD cards derive their entire state from the computed upload_editor_status
  // (+ optimistic client state), never from raw batch_status — see getSimplifiedUploadPreviewState.
  // Everything else (recordings, live events, and all cards when the flag is off) uses the legacy
  // batch_status pipeline below.
  if (isSimplifiedUploadEnabled() && session.media_source_type === 'UPLOAD') {
    return getSimplifiedUploadPreviewState(session);
  }

  const projectId = session.project.id || session.project_id;
  const sessionId = session.id;

  const { isContentUploadInProgress, contentStatusText } = getSessionPreviewDetails(session);
  const canPreviewSession = isSessionReadyForPreview(session);

  const isRecordingEventProcessing =
    isEventTypeRecording(session.event_type) && session.individual_recording_status === 'PROCESSING';

  const canOpenSession = canPreviewSession || isContentUploadInProgress;

  const isVideoReadyForPreview =
    ['DONE', 'VOD_DONE'].includes(session.batch_status) ||
    (session.event_type === 'Recording' && session.individual_recording_status === 'DONE');

  const isDisabled = !isVideoReadyForPreview && !isContentUploadInProgress;

  const isBatchFailed =
    session.batch_status === 'FAILED' || session.batch_status === 'MULTI_LANGUAGE_SUPPORT_RESTRICTED';
  const failedIconName = session.reason === 'LANGUAGE_NOT_SUPPORTED' ? 'IconLanguageOff' : 'IconSquareRoundedX';

  const endTime =
    session.broadcast_type === 'LIVE_HYBRID' ? session.live_end_time || session.end_time : session.end_time;

  return {
    projectId,
    sessionId,
    isContentUploadInProgress,
    contentStatusText,
    canPreviewSession,
    canOpenSession,
    isRecordingEventProcessing,
    isVideoReadyForPreview,
    isDisabled,
    isBatchFailed,
    failedIconName,
    endTime,
    newUploadBadge: null
  };
}
